import axios from 'axios';
import API_CONFIG from '../config/api.config';

/**
 * Отдельный клиент для микросервиса баркодов (dacha-modules).
 * Запросы идут на same-origin прокси /barcode-api (server.js),
 * который подставляет api_token и форвардит в модуль.
 */
const barcodeClient = axios.create({
  baseURL: API_CONFIG.BARCODE_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Формат ответов модуля: { success: true, payload } / { success: false, error }
 * Ошибки приходят с HTTP 500 - достаём текст из тела ответа
 */
barcodeClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.error || error.message;
    return Promise.reject(new Error(message));
  }
);

const unwrap = (response) => {
  if (!response.data?.success) {
    throw new Error(response.data?.error || 'Ошибка сервиса баркодов');
  }
  return response.data.payload;
};

const barcodeApi = {
  /**
   * Список товаров (типов товаров) из справочника модуля
   * @returns {Promise<Array>} Список товаров
   */
  getProducts: async () => {
    const payload = unwrap(await barcodeClient.get('/barcode/products'));
    return payload.products || [];
  },

  /**
   * Партии баркодов товара: [{ batch, product_id, amount }]
   * @param {number} productId - Идентификатор товара
   * @returns {Promise<Array>} Партии баркодов: [{ batch, product_id, amount }]
   */
  getBatches: async (productId) => {
    const payload = unwrap(await barcodeClient.get(`/barcode/batches?product_id=${productId}`));
    return payload.barcodes || [];
  },

  /**
   * Запуск генерации партии. Ответ приходит сразу, вставка кодов идёт асинхронно
   * @param {string} productCode - Код товара
   * @param {number} barcodeCount - Количество генерируемых кодов
   * @returns {Promise<Object>} Результат запуска генерации: { batch, product, count, executeTime }
   */
  generateBatch: async (productCode, barcodeCount) => {
    const payload = unwrap(await barcodeClient.get(`/barcode/generate?product_code=${productCode}&barcode_count=${barcodeCount}`));
    return payload;
  },

  /**
   * Прогресс вставки кодов партии: { count }
   * @param {number} productId - Идентификатор товара
   * @param {string} batch - Номер партии
   * @returns {Promise<number>} Количество вставленных кодов
   */
  getGenerateProgress: async (productId, batch) => {
    const payload = unwrap(await barcodeClient.get(`/barcode/generate/progress?product=${productId}&batch=${batch}`));
    return payload.count || 0;
  },

  /**
   * Коды партии с пагинацией: { count, barcodes: { data, current_page, ... } }
   * @param {number} productId - Идентификатор товара
   * @param {string} batch - Номер партии
   * @param {number} [page=1] - Номер страницы
   * @param {number} [limit=10000] - Размер страницы
   * @returns {Promise<Object>} Коды партии с пагинацией: { count, barcodes: { data, current_page, ... } }
   */
  getBatchBarcodes: async (productId, batch, page = 1, limit = 10000) => {
    const payload = unwrap(await barcodeClient.get(`/barcode/generated?product=${productId}&batch=${batch}&page=${page}&limit=${limit}`));
    return payload;
  },

  /**
   * Тестовая проверка кода без отметки в базе: { status, statusMessage, barcode }
   * status: 0 - был ранее введен, 1 - подлинный, 2 - код не соответствует товару, 3 - подделка
   * mode=admin передаём в query - именно там его проверяет модуль
   * @param {string} barcode - Проверяемый штрихкод
   * @returns {Promise<Object>} Результат проверки: { status, statusMessage, barcode }
   */
  verifyBarcode: async (barcode) => {
    const payload = unwrap(await barcodeClient.post('/barcode/verify?mode=admin', { barcode }));
    return payload;
  },

  /**
   * URL серверного экспорта партии (файл формирует и стримит модуль)
   * @param {number} productId - Идентификатор товара
   * @param {string} batch - Номер партии
   * @param {string} format - Формат экспорта
   * @returns {string} URL для скачивания файла экспорта
   */
  getExportUrl: (productId, batch, format) =>
    `${API_CONFIG.BARCODE_API_URL}/barcode/export?product_id=${productId}&batch=${batch}&format=${format}`,

  /**
   * Удаление всей партии: { count }
   * @param {number} productId - Идентификатор товара
   * @param {string} batch - Номер партии
   * @returns {Promise<number>} Количество удалённых кодов
   */
  deleteBatch: async (productId, batch) => {
    const payload = unwrap(await barcodeClient.post('/barcode/delete/list', { product_id: productId, batch }));
    return payload.count || 0;
  },

  /**
   * Принудительное обновление кэша товаров модуля из Mongo.
   * Вызывается после CRUD-операций с товарами в разделе «Товары»
   * @returns {Promise<number>} Количество товаров в кэше
   */
  refreshProducts: async () => {
    const payload = unwrap(await barcodeClient.post('/barcode/products/refresh'));
    return payload.count || 0;
  },
};

export default barcodeApi;